import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../api/client';
import type { BlockDto, ItemDto, Visibility } from '../api/types';
import { AddItemDialog } from '../components/AddItemDialog';
import { Cover } from '../components/cards';
import {
  IconArrowDown,
  IconArrowUp,
  IconGrip,
  IconLink,
  IconLock,
  IconPlus,
  IconSearch,
  IconText,
  IconTrash,
  TypeIcon,
} from '../components/icons';
import { Modal, ConfirmDialog } from '../components/Modal';
import { useToast } from '../components/Toast';
import { EmptyState, ErrorState, Field, Loading, Segmented, TextArea, TextInput, TypeBadge } from '../components/ui';
import { useAsync, useDebounced } from '../hooks/useAsync';
import { t } from '../i18n';
import { hostOf, messageOf, relativeTime, visibilityOptions } from '../lib/format';

export function PlaylistEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const state = useAsync(() => api.playlists.get(id), [id]);

  const [blocks, setBlocks] = useState<BlockDto[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [metaDirty, setMetaDirty] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [addingText, setAddingText] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const reorderTimer = useRef<number | null>(null);
  const [savedOrder, setSavedOrder] = useState<string[]>([]);
  const blocksRef = useRef<BlockDto[]>([]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    const playlist = state.data;
    if (!playlist) return;
    setBlocks(playlist.blocks);
    setTitle(playlist.title);
    setDescription(playlist.description ?? '');
    setVisibility(playlist.visibility);
    setCoverImageUrl(playlist.coverImageUrl ?? '');
    setSavedOrder(playlist.blocks.map((block) => block.id));
  }, [state.data]);

  const playlist = state.data;

  const sensors = useSensors(
    // A tap must still open editors, so dragging starts only after a few pixels.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const orderChanged = useMemo(
    () => blocks.length !== savedOrder.length || blocks.some((block, index) => block.id !== savedOrder[index]),
    [blocks, savedOrder],
  );

  if (state.error) {
    return (
      <div className="page">
        <ErrorState error={state.error} onRetry={state.reload} />
      </div>
    );
  }
  if (state.loading || !playlist) {
    return (
      <div className="page">
        <Loading rows={5} />
      </div>
    );
  }
  if (!playlist.viewer.canEdit) {
    return (
      <div className="page page--read">
        <EmptyState
          icon={<IconLock />}
          title={t('editor.lockedTitle')}
          body={t('editor.lockedBody')}
          action={
            <Link className="btn btn--outline" to={`/p/${playlist.id}`}>
              {t('editor.openPlaylist')}
            </Link>
          }
        />
      </div>
    );
  }

  /** Push the visible order to the server, debounced so a long drag costs one request. */
  function scheduleReorder() {
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current);
    reorderTimer.current = window.setTimeout(async () => {
      const ids = blocksRef.current.map((block) => block.id);
      try {
        await api.playlists.reorder(playlist!.id, ids);
        setSavedOrder(ids);
      } catch (error) {
        toast.error(messageOf(error));
      }
    }, 450);
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = blocks.findIndex((block) => block.id === active.id);
    const to = blocks.findIndex((block) => block.id === over.id);
    if (from < 0 || to < 0) return;
    setBlocks(arrayMove(blocks, from, to));
    scheduleReorder();
  }

  async function saveMeta() {
    if (!title.trim()) {
      toast.error(t('editor.titleRequired'));
      return;
    }
    setSavingMeta(true);
    try {
      const updated = await api.playlists.update(playlist!.id, {
        title: title.trim(),
        description,
        visibility,
        coverImageUrl,
      });
      setMetaDirty(false);
      toast.success(t('editor.saved'));
      if (updated.updatedAt) state.reload();
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setSavingMeta(false);
    }
  }

  async function appendItem(itemId: string) {
    try {
      const block = await api.playlists.addBlock(playlist!.id, { type: 'item', itemId });
      setBlocks((current) => [...current, block]);
      setSavedOrder((current) => [...current, block.id]);
      toast.success(t('editor.itemAppended'));
    } catch (error) {
      toast.error(messageOf(error));
    }
  }

  async function addText() {
    const content = textDraft.trim();
    if (!content) return;
    try {
      const block = await api.playlists.addBlock(playlist!.id, { type: 'text', content });
      setBlocks((current) => [...current, block]);
      setSavedOrder((current) => [...current, block.id]);
      setTextDraft('');
      setAddingText(false);
    } catch (error) {
      toast.error(messageOf(error));
    }
  }

  async function updateText(blockId: string, content: string) {
    try {
      const updated = await api.playlists.updateBlock(playlist!.id, blockId, { content });
      setBlocks((current) => current.map((block) => (block.id === blockId ? updated : block)));
    } catch (error) {
      toast.error(messageOf(error));
      state.reload();
    }
  }

  async function removeBlock(blockId: string) {
    const next = blocks.filter((block) => block.id !== blockId);
    setBlocks(next);
    try {
      await api.playlists.deleteBlock(playlist!.id, blockId);
      setSavedOrder(next.map((block) => block.id));
    } catch (error) {
      toast.error(messageOf(error));
      state.reload();
    }
  }

  async function move(blockId: string, direction: -1 | 1) {
    const from = blocks.findIndex((block) => block.id === blockId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= blocks.length) return;
    const next = arrayMove(blocks, from, to);
    setBlocks(next);
    scheduleReorder();
  }

  async function destroy() {
    try {
      await api.playlists.remove(playlist!.id);
      toast.success(t('editor.deleted'));
      navigate('/app/playlists', { replace: true });
    } catch (error) {
      toast.error(messageOf(error));
    }
  }

  const activeBlock = activeId ? blocks.find((block) => block.id === activeId) ?? null : null;
  const noteCount = blocks.filter((block) => block.type === 'text').length;

  /** Screen readers get titles and places, never the internal block ids dnd-kit would announce. */
  const labelOf = (id: string | number): string => {
    const index = blocks.findIndex((block) => String(block.id) === String(id));
    if (index < 0) return t('editor.blockWord');
    const block = blocks[index];
    const name =
      block.type === 'item' ? block.item?.title : block.content?.slice(0, 60);
    return t('editor.blockLabel', {
      name: name || (block.type === 'item' ? t('editor.fallbackItem') : t('editor.fallbackNote')),
      n: index + 1,
    });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="tiny" style={{ marginBottom: 'var(--sp-2)' }}>
            <Link to={`/p/${playlist.id}`}>{t('editor.previewLink')}</Link>
          </p>
          <h1 style={{ fontSize: 'var(--fs-24)' }}>{t('editor.heading')}</h1>
        </div>
        <span className="tiny muted">
          {t('editor.blocksCount', { n: blocks.length })}
          {noteCount ? t('editor.notesSuffix', { n: noteCount }) : ''} ·{' '}
          {t('playlist.updated', { time: relativeTime(playlist.updatedAt) })}
        </span>
      </div>

      <div className="editor">
        <div className="stack">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            accessibility={{
              announcements: {
                onDragStart: ({ active }) => t('editor.dndStart', { label: labelOf(active.id) }),
                onDragOver: ({ active, over }) =>
                  over
                    ? t('editor.dndOver', {
                        label: labelOf(active.id),
                        n: blocks.findIndex((block) => String(block.id) === String(over.id)) + 1,
                      })
                    : '',
                onDragEnd: ({ active, over }) =>
                  over && String(active.id) !== String(over.id)
                    ? t('editor.dndEnd', {
                        label: labelOf(active.id),
                        n: blocks.findIndex((block) => String(block.id) === String(over.id)) + 1,
                      })
                    : t('editor.dndUnchanged'),
                onDragCancel: () => t('editor.dndCancelled'),
              },
            }}
            onDragStart={(event: DragStartEvent) => setActiveId(String(event.active.id))}
            onDragEnd={onDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext items={blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
              <div className="editor__list">
                {blocks.map((block, index) => (
                  <SortableBlock
                    key={block.id}
                    block={block}
                    index={index}
                    total={blocks.length}
                    onMove={move}
                    onDelete={removeBlock}
                    onText={updateText}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay className="drag-overlay" dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' }}>
              {activeBlock ? (
                <div className="editor-row">
                  <span className="editor-row__grip">
                    <IconGrip size={18} />
                  </span>
                  <div className="editor-row__main">
                    {activeBlock.type === 'item' && activeBlock.item ? (
                      <span className="row row--tight">
                        <TypeIcon type={activeBlock.item.type} size={16} />
                        <span className="strong clamp-1">{activeBlock.item.title}</span>
                      </span>
                    ) : (
                      <span className="clamp-1 dim">{activeBlock.content || t('editor.noteLabel')}</span>
                    )}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {blocks.length === 0 ? (
            <p className="notice" style={{ marginTop: 'var(--sp-4)' }}>
              <IconGrip size={16} />
              <span>{t('editor.emptyBlocks')}</span>
            </p>
          ) : (
            <p className="tiny muted" style={{ marginTop: 'var(--sp-3)' }}>
              {t('editor.gripHint')}
            </p>
          )}

          <div className="row" style={{ marginTop: 'var(--sp-5)' }}>
            <button type="button" className="btn btn--primary" onClick={() => setPickerOpen(true)}>
              <IconLink size={17} /> {t('editor.addFromLibrary')}
            </button>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                setAddingText((value) => !value);
                setTextDraft('');
              }}
              aria-expanded={addingText}
            >
              <IconText size={17} /> {t('editor.addMyNote')}
            </button>
          </div>

          {addingText ? (
            <div className="panel stack-sm" style={{ padding: 'var(--sp-4)' }}>
              <Field label={t('editor.textField')} htmlFor="new-text" hint={t('editor.textHint')}>
                <TextArea
                  id="new-text"
                  rows={3}
                  value={textDraft}
                  onChange={(event) => setTextDraft(event.target.value)}
                  placeholder={t('editor.textPlaceholder')}
                />
              </Field>
              <div className="row">
                <button type="button" className="btn btn--primary btn--sm" onClick={addText} disabled={!textDraft.trim()}>
                  {t('editor.appendToEnd')}
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAddingText(false)}>
                  {t('ui.cancel')}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <aside className="editor__aside stack">
          <Field label={t('playlists.fieldTitle')} htmlFor="edit-title">
            <TextInput
              id="edit-title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                setMetaDirty(true);
              }}
            />
          </Field>
          <Field label={t('playlists.fieldDescription')} htmlFor="edit-desc">
            <TextArea
              id="edit-desc"
              rows={3}
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
                setMetaDirty(true);
              }}
            />
          </Field>
          <Field label={t('editor.coverLabel')} htmlFor="edit-cover" hint={t('editor.coverHint')}>
            <TextInput
              id="edit-cover"
              type="url"
              value={coverImageUrl}
              onChange={(event) => {
                setCoverImageUrl(event.target.value);
                setMetaDirty(true);
              }}
            />
          </Field>
          <Segmented
            name="edit-visibility"
            label={t('editor.visibility')}
            value={visibility}
            onChange={(value) => {
              setVisibility(value);
              setMetaDirty(true);
            }}
            options={visibilityOptions().map((option) => ({ value: option.value, label: option.label }))}
          />
          <p className="field__hint">{visibilityOptions().find((option) => option.value === visibility)?.hint}</p>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={saveMeta}
            disabled={savingMeta || !metaDirty}
          >
            {savingMeta ? t('item.saving') : metaDirty ? t('editor.saveDescription') : t('editor.allSaved')}
          </button>
          <button type="button" className="btn btn--danger btn--sm" onClick={() => setConfirmLeave(true)}>
            <IconTrash size={15} /> {t('editor.deletePlaylist')}
          </button>
        </aside>
      </div>

      <div className="editor__bar">
        <span className="editor__status">
          <span className="dot" aria-hidden="true" />
          {orderChanged || metaDirty ? t('editor.unsaved') : t('editor.orderSaved')}
        </span>
        <div className="row row--tight">
          <Link className="btn btn--ghost btn--sm" to={`/p/${playlist.id}`}>
            {t('editor.preview')}
          </Link>
          <button type="button" className="btn btn--primary btn--sm" onClick={saveMeta} disabled={savingMeta}>
            {savingMeta ? t('item.saving') : t('item.save')}
          </button>
        </div>
      </div>

      <ItemPickerDialog
        open={pickerOpen}
        savedIds={blocks.flatMap((block) => (block.item ? [block.item.id] : []))}
        onClose={() => setPickerOpen(false)}
        onPick={(itemId) => {
          void appendItem(itemId);
        }}
        onNew={() => {
          setPickerOpen(false);
          setAddOpen(true);
        }}
      />

      <AddItemDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(itemId) => {
          setAddOpen(false);
          void appendItem(itemId);
        }}
      />

      <ConfirmDialog
        open={confirmLeave}
        title={t('editor.confirmTitle')}
        description={t('editor.confirmBody', { title })}
        onClose={() => setConfirmLeave(false)}
        onConfirm={destroy}
      />
    </div>
  );
}

function SortableBlock({
  block,
  index,
  total,
  onMove,
  onDelete,
  onText,
}: {
  block: BlockDto;
  index: number;
  total: number;
  onMove: (id: string, direction: -1 | 1) => void;
  onDelete: (id: string) => void;
  onText: (id: string, content: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(block.content ?? '');

  useEffect(() => {
    setValue(block.content ?? '');
  }, [block.content]);

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`editor-row${isDragging ? ' editor-row--dragging' : ''}`}>
      <button
        type="button"
        className="editor-row__grip"
        aria-label={t('editor.gripAria', { n: index + 1, total })}
        {...attributes}
        {...listeners}
      >
        <IconGrip size={18} />
      </button>

      <div className="editor-row__main">
        {block.type === 'item' && block.item ? (
          <a
            className="row row--top"
            href={block.item.url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ gap: 'var(--sp-3)', color: 'inherit' }}
          >
            <Cover
              src={block.item.imageUrl}
              title={block.item.title}
              className="editor-row__cover cover--portrait"
            />
            <span style={{ minWidth: 0 }}>
              <span className="strong clamp-1">{block.item.title}</span>
              <span className="row row--tight tiny muted" style={{ marginTop: 2 }}>
                <TypeBadge type={block.item.type} />
                {block.item.sourceName ?? hostOf(block.item.url)}
              </span>
            </span>
          </a>
        ) : editing ? (
          <div className="stack-sm">
            <TextArea
              rows={3}
              value={value}
              aria-label={t('editor.noteTextareaAria')}
              onChange={(event) => setValue(event.target.value)}
              onBlur={() => {
                setEditing(false);
                if (value.trim() !== (block.content ?? '').trim()) onText(block.id, value.trim());
              }}
            />
            <p className="tiny muted">{t('editor.noteBlurHint')}</p>
          </div>
        ) : (
          <button
            type="button"
            className="block__text"
            style={{ border: 0, background: 'transparent', textAlign: 'left', cursor: 'text', padding: 0 }}
            onClick={() => setEditing(true)}
          >
            {block.content || t('editor.emptyNote')}
          </button>
        )}
      </div>

      <div className="editor-row__tools">
        <span className="editor-row__index">{index + 1}</span>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => onMove(block.id, -1)}
          disabled={index === 0}
          aria-label={t('editor.moveUp')}
        >
          <IconArrowUp size={16} />
        </button>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => onMove(block.id, 1)}
          disabled={index === total - 1}
          aria-label={t('editor.moveDown')}
        >
          <IconArrowDown size={16} />
        </button>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => onDelete(block.id)}
          aria-label={block.type === 'item' ? t('editor.removeBlock') : t('editor.removeNote')}
        >
          <IconTrash size={16} />
        </button>
      </div>
    </div>
  );
}

/** Library chooser — the editor adds existing links, it does not re-parse them. */
function ItemPickerDialog({
  open,
  savedIds,
  onClose,
  onPick,
  onNew,
}: {
  open: boolean;
  savedIds: string[];
  onClose: () => void;
  onPick: (itemId: string) => void;
  onNew: () => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const [items, setItems] = useState<ItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const saved = useMemo(() => new Set(savedIds), [savedIds]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    api.items
      .list({ limit: 30, ...(debounced.trim() ? { q: debounced.trim() } : {}) })
      .then((page) => {
        if (active) setItems(page.items);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, debounced]);

  return (
    <Modal
      open={open}
      title={t('editor.addFromLibrary')}
      description={t('editor.pickerBody')}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn--outline" onClick={onNew}>
          <IconPlus size={16} /> {t('editor.newSave')}
        </button>
      }
    >
      <div className="search-field">
        <span className="search-field__icon">
          <IconSearch size={18} />
        </span>
        <TextInput
          type="search"
          aria-label={t('editor.pickSearchAria')}
          placeholder={t('editor.pickSearchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {loading && items.length === 0 ? (
        <Loading rows={3} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('editor.pickEmptyTitle')}
          body={t('editor.pickEmptyBody')}
        />
      ) : (
        <ul className="stack-sm" style={{ maxHeight: '46vh', overflow: 'auto' }}>
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" className="list-row" onClick={() => onPick(item.id)}>
                <span className="badge">
                  <TypeIcon type={item.type} size={13} />
                </span>
                <span className="user-row__main">
                  <span className="strong clamp-1">{item.title}</span>
                  <span className="tiny muted clamp-1">{hostOf(item.url)}</span>
                </span>
                {saved.has(item.id) ? <span className="badge">{t('editor.alreadyHere')}</span> : <IconPlus size={16} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
